import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import './AccessControl.css';

const DEFAULT_POLICY = {
  whitelist_enabled: true,
  whitelist_domains: [],
  whitelist_emails: [],
  blacklist_emails: [],
};

const AccessControl = () => {
  const { apiCall } = useApi();
  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [domainInput, setDomainInput] = useState('');
  const [whitelistEmailInput, setWhitelistEmailInput] = useState('');
  const [blacklistEmailInput, setBlacklistEmailInput] = useState('');

  // 204-safe wrapper
  const apiCallNoBody = async (endpoint, options) => {
    try { return await apiCall(endpoint, options); }
    catch (err) { if (err instanceof SyntaxError) return null; throw err; }
  };

  // Normalize server response into local policy shape
  const applyPolicyResponse = (data) => {
    if (!data) return;
    setPolicy({
      whitelist_enabled: data.whitelist_enabled ?? true,
      whitelist_domains: data.whitelist_domains ?? [],
      whitelist_emails: data.whitelist_emails ?? [],
      blacklist_emails: data.blacklist_emails ?? [],
      updated_at: data.updated_at,
      updated_by: data.updated_by,
    });
  };

  const fetchPolicy = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiCall('/admin/access-control/policy');
      applyPolicyResponse(data);
    } catch (err) {
      // 404 = policy not initialized yet. Treat as empty, start fresh.
      if (/not found/i.test(err.message)) {
        setPolicy(DEFAULT_POLICY);
      } else {
        setError(err.message);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 2500);
  };

  const handleToggleWhitelist = async () => {
    setBusy(true); setError('');
    try {
      const data = await apiCall('/admin/access-control/policy', {
        method: 'PATCH',
        body: JSON.stringify({ whitelist_enabled: !policy.whitelist_enabled }),
      });
      applyPolicyResponse(data);
      flashSuccess(`Whitelist ${!policy.whitelist_enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  const addItem = async (endpoint, value, setInput, successMsg) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return;
    setBusy(true); setError('');
    try {
      const data = await apiCall(`/admin/access-control/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify({ value: trimmed }),
      });
      applyPolicyResponse(data);
      setInput('');
      flashSuccess(successMsg);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  const removeItem = async (endpoint, value, successMsg) => {
    setBusy(true); setError('');
    try {
      await apiCallNoBody(`/admin/access-control/${endpoint}/${encodeURIComponent(value)}`, { method: 'DELETE' });
      await fetchPolicy();
      flashSuccess(successMsg);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  const addDomain = () => addItem('whitelist/domains', domainInput, setDomainInput, 'Domain added.');
  const removeDomain = (v) => removeItem('whitelist/domains', v, 'Domain removed.');
  const addWhitelistEmail = () => addItem('whitelist/emails', whitelistEmailInput, setWhitelistEmailInput, 'Email added.');
  const removeWhitelistEmail = (v) => removeItem('whitelist/emails', v, 'Email removed.');
  const addBlacklistEmail = () => addItem('blacklist/emails', blacklistEmailInput, setBlacklistEmailInput, 'Email blocked.');
  const removeBlacklistEmail = (v) => removeItem('blacklist/emails', v, 'Email unblocked.');

  const handleKeyDown = (e, handler) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handler();
    }
  };

  const formatTimestamp = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
  };

  if (loading) return <Loader />;

  return (
    <div className="access-control-page">
      <div className="page-header">
        <h2 className="page-title">Access Control</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Whitelist Card */}
      <div className="settings-card">
        <div className="ac-card-header">
          <h3 className="card-title">Email Whitelist</h3>
          <div className="ac-toggle-wrapper">
            <span className="ac-toggle-label">
              {policy.whitelist_enabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              className={`ac-toggle ${policy.whitelist_enabled ? 'ac-toggle-on' : ''}`}
              onClick={handleToggleWhitelist}
              disabled={busy}
            >
              <span className="ac-toggle-thumb" />
            </button>
          </div>
        </div>

        <div className="ac-section">
          <label className="form-label">Allowed Domains</label>
          <p className="ac-hint">Users with emails from these domains will be allowed access.</p>
          <div className="ac-input-row">
            <input
              type="text"
              className="form-input ac-input"
              placeholder="e.g. qarl.ai"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, addDomain)}
              disabled={busy}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={addDomain}
              disabled={busy || !domainInput.trim()}
            >
              Add
            </button>
          </div>
          <div className="ac-chips">
            {policy.whitelist_domains.map((domain) => (
              <span key={domain} className="ac-chip ac-chip-domain">
                @{domain}
                <button className="ac-chip-remove" onClick={() => removeDomain(domain)} disabled={busy}>
                  &times;
                </button>
              </span>
            ))}
            {policy.whitelist_domains.length === 0 && (
              <span className="ac-empty">No domains added.</span>
            )}
          </div>
        </div>

        <div className="ac-section">
          <label className="form-label">Whitelisted Emails</label>
          <p className="ac-hint">Individual email addresses to allow, regardless of domain.</p>
          <div className="ac-input-row">
            <input
              type="email"
              className="form-input ac-input"
              placeholder="e.g. user@example.com"
              value={whitelistEmailInput}
              onChange={(e) => setWhitelistEmailInput(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, addWhitelistEmail)}
              disabled={busy}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={addWhitelistEmail}
              disabled={busy || !whitelistEmailInput.trim()}
            >
              Add
            </button>
          </div>
          <div className="ac-chips">
            {policy.whitelist_emails.map((email) => (
              <span key={email} className="ac-chip ac-chip-email">
                {email}
                <button className="ac-chip-remove" onClick={() => removeWhitelistEmail(email)} disabled={busy}>
                  &times;
                </button>
              </span>
            ))}
            {policy.whitelist_emails.length === 0 && (
              <span className="ac-empty">No emails added.</span>
            )}
          </div>
        </div>
      </div>

      {/* Blacklist Card */}
      <div className="settings-card">
        <h3 className="card-title">Email Blacklist</h3>
        <div className="ac-section">
          <label className="form-label">Blocked Emails</label>
          <p className="ac-hint">These email addresses will be denied access, even if their domain is whitelisted.</p>
          <div className="ac-input-row">
            <input
              type="email"
              className="form-input ac-input"
              placeholder="e.g. banned@qarl.dev"
              value={blacklistEmailInput}
              onChange={(e) => setBlacklistEmailInput(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, addBlacklistEmail)}
              disabled={busy}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={addBlacklistEmail}
              disabled={busy || !blacklistEmailInput.trim()}
            >
              Add
            </button>
          </div>
          <div className="ac-chips">
            {policy.blacklist_emails.map((email) => (
              <span key={email} className="ac-chip ac-chip-blocked">
                {email}
                <button className="ac-chip-remove" onClick={() => removeBlacklistEmail(email)} disabled={busy}>
                  &times;
                </button>
              </span>
            ))}
            {policy.blacklist_emails.length === 0 && (
              <span className="ac-empty">No emails blocked.</span>
            )}
          </div>
        </div>
      </div>

      {/* Metadata */}
      {(policy.updated_at || policy.updated_by) && (
        <div className="settings-card ac-meta-card">
          <div className="ac-meta-row">
            <span className="ac-meta-label">Last updated</span>
            <span className="ac-meta-value">{formatTimestamp(policy.updated_at)}</span>
          </div>
          <div className="ac-meta-row">
            <span className="ac-meta-label">Updated by</span>
            <span className="ac-meta-value">{policy.updated_by || '—'}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControl;
